import React, { useState, useEffect } from "react";
import { EditButton, ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Space, Tag, Input, Button, Tooltip, Modal, Form, Select, message, Card } from "antd";
const { TextArea } = Input;
import { SearchOutlined, PrinterOutlined, SyncOutlined, PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";

interface Sale {
    id: number;
    invoiceNo: string;
    customerTPIN: string;
    customerName: string;
    currencyTypeCode: string;
    totalAmount: number;
    totalTax: number;
    status: string;
    createdAt: string;
    documentUrl?: string;
}

export const SaleList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Sale[]>([]);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "sales",
    });

    useEffect(() => {
        if (!data?.data) return;

        let sales = data.data as Sale[];
        
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            sales = sales.filter((sale: Sale) =>
                sale.invoiceNo?.toLowerCase().includes(searchLower) ||
                sale.customerName?.toLowerCase().includes(searchLower) ||
                sale.customerTPIN?.toLowerCase().includes(searchLower) ||
                sale.status?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...sales].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handlePrint = (record: Sale) => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        }
    };

    const handleRegenerateInvoice = (record: Sale) => {
        setSelectedSale(record);
        setRegenerateModalVisible(true);
        form.resetFields();
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!selectedSale) return;

        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false
            };

            const result = await invoiceRegenerationService.regenerateSaleInvoice(selectedSale.id, request);

            if (result.success) {
                message.success(`Invoice ${selectedSale.invoiceNo} regenerated successfully!`);
                setRegenerateModalVisible(false);
                setSelectedSale(null);
                window.location.reload();
            } else {
                message.error(`Failed to regenerate invoice: ${result.errorMessage}`);
            }
        } catch (error: any) {
            message.error(`Failed to regenerate invoice: ${error.response?.data?.message || error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING": return "warning";
            case "COMPLETED": return "success";
            case "CANCELLED": return "error";
            default: return "default";
        }
    };

    const columns = [
        {
            title: "Invoice #",
            dataIndex: "invoiceNo",
            key: "invoiceNo",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value || "-"}</span>
            ),
            sorter: (a: Sale, b: Sale) => (a.invoiceNo || "").localeCompare(b.invoiceNo || ""),
        },
        {
            title: "Customer",
            dataIndex: "customerName",
            key: "customerName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            sorter: (a: Sale, b: Sale) => (a.customerName || "").localeCompare(b.customerName || ""),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={getStatusColor(value)} className="status-tag">{value}</Tag>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            render: (_: any, record: Sale) => (
                <span className="font-semibold text-primary">
                    {record.currencyTypeCode} {record.totalAmount?.toFixed(2) || '0.00'}
                </span>
            ),
            sorter: (a: Sale, b: Sale) => (a.totalAmount || 0) - (b.totalAmount || 0),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a: Sale, b: Sale) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 140,
            render: (_: any, record: Sale) => (
                <Space size="small">
                    {record.status === "PENDING" && (
                        <EditButton hideText size="small" recordItemId={record.id} />
                    )}
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    {record?.documentUrl && record?.status === "COMPLETED" && (
                        <Tooltip title="Print">
                            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
                        </Tooltip>
                    )}
                    {record?.documentUrl && record?.status === "COMPLETED" && (
                        <Tooltip title="Regenerate">
                            <Button size="small" icon={<SyncOutlined />} onClick={() => handleRegenerateInvoice(record)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Sales"
                icon={<ShopOutlined />}
                breadcrumbs={[{ title: "Sales" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/sales/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by invoice, customer, or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>

            <Modal
                title={`Regenerate Invoice ${selectedSale?.invoiceNo || ''}`}
                open={regenerateModalVisible}
                onCancel={() => { setRegenerateModalVisible(false); setSelectedSale(null); form.resetFields(); }}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleRegenerateSubmit} initialValues={{ regenerationType: 'BugFix' }}>
                    <Form.Item name="regenerationType" label="Regeneration Type" rules={[{ required: true }]}>
                        <Select placeholder="Select regeneration type">
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="LayoutUpdate">Layout Update</Select.Option>
                            <Select.Option value="Manual">Manual</Select.Option>
                            <Select.Option value="DataCorrection">Data Correction</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reason" label="Reason (Optional)">
                        <TextArea rows={3} placeholder="Provide a brief explanation" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>
                                {regenerating ? 'Regenerating...' : 'Regenerate Invoice'}
                            </Button>
                            <Button onClick={() => { setRegenerateModalVisible(false); setSelectedSale(null); }}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
